import { useEffect, useState, useCallback, useRef } from 'react';
import { getAccessToken } from '@acitra/api-client';
import type { WebSocketConnectionState, WebSocketMessageHandler } from '@acitra/shared';
import { useAuthStore } from '../stores/auth-store';
import { wsService } from '../services/websocket';

/**
 * Hook to manage WebSocket connection and subscriptions
 *
 * Automatically connects when authenticated and tenant is selected,
 * disconnects on logout or tenant change.
 */
export function useWebSocket() {
  const { isAuthenticated, currentTenantId } = useAuthStore();
  const [connectionState, setConnectionState] = useState<WebSocketConnectionState>('disconnected');
  const connectAttemptedRef = useRef(false);
  const previousTenantIdRef = useRef<string | null>(null);

  // Connect/disconnect based on auth state
  useEffect(() => {
    // Track connection state
    const unsubscribeState = wsService.onStateChange(setConnectionState);

    // Only attempt connection when authenticated with a tenant
    if (isAuthenticated && currentTenantId) {
      // Detect tenant change and force reconnect
      const tenantChanged =
        previousTenantIdRef.current !== null && previousTenantIdRef.current !== currentTenantId;

      if (tenantChanged) {
        // Disconnect from old tenant before connecting to new one
        wsService.disconnect();
        connectAttemptedRef.current = false;
      }

      previousTenantIdRef.current = currentTenantId;

      // Prevent duplicate connection attempts
      if (connectAttemptedRef.current) {
        return () => unsubscribeState();
      }
      connectAttemptedRef.current = true;

      // Get access token and connect
      getAccessToken().then((token) => {
        if (token && currentTenantId) {
          wsService.connect(token, currentTenantId);
        }
      });
    } else {
      // Disconnect when logged out or no tenant
      connectAttemptedRef.current = false;
      previousTenantIdRef.current = null;
      wsService.disconnect();
    }

    return () => {
      unsubscribeState();
    };
  }, [isAuthenticated, currentTenantId]);

  // Subscribe to a message type
  const subscribe = useCallback(
    <T = unknown>(type: string, handler: WebSocketMessageHandler<T>): (() => void) => {
      return wsService.subscribe(type, handler);
    },
    []
  );

  return {
    connectionState,
    isConnected: connectionState === 'connected',
    subscribe,
  };
}

/**
 * Hook to subscribe to a specific WebSocket message type
 *
 * Automatically manages subscription lifecycle based on component mount.
 *
 * @example
 * useWebSocketSubscription('invoice.updated', (data) => {
 *   console.log('Invoice updated:', data);
 * });
 */
export function useWebSocketSubscription<T = unknown>(
  type: string,
  handler: WebSocketMessageHandler<T>,
  deps: React.DependencyList = []
) {
  const handlerRef = useRef(handler);

  // Keep handler ref updated
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    const wrappedHandler = (data: T) => {
      handlerRef.current(data);
    };

    const unsubscribe = wsService.subscribe(type, wrappedHandler);
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, ...deps]);
}
